/** Console v0.5: Meetings (design brief v0.5, the Meetings mock-up): the meetings list beside one meeting's notes.
 * The notes pane is today's notes review (meetings-ui.ts mountSummary) unchanged -- source checks, edit, approve and
 * hand over, the full transcript -- so nothing that works today is lost -- with the recorder's name in place of "Host"
 * (A s.18). The ⋯ menu: Download notes (the server builds and audits the file, A's notes.txt) and Rename (host or
 * admin); Delete meeting joins it with A's purge route. Recording opens today's recorder until H11. */

import { ApiError, apiRequest, fetchMeeting, fetchMeetings, fetchSummary } from "../api.js";
import type { MeetingRow } from "../api.js";
import { mountSummary, retentionNote } from "../meetings-ui.js";
import type { Mounted } from "../meetings-ui.js";
import { buildUrl } from "../nav.js";
import type { ScreenContext } from "./app.js";
import { SCREENS } from "./app.js";
import { DELETE_COPY, MEETINGS_V05_COPY, downloadName, meetingStateLabel, matchesMeeting, renameProblem, sortMeetings } from "./meetings-logic.js";
import { emptyState, figure, h, legalFooter, listRow, loading, menuButton, notice, openDialog, pageHead, splitPane, uid } from "./ui.js";
import { shortDate } from "./team-logic.js";

function messageOf(err: unknown, fallback: string): string {
  return err instanceof ApiError ? err.message : fallback;
}

function meetings(ctx: ScreenContext): () => void {
  let rows: MeetingRow[] | null = null;
  let error: string | null = null;
  let query = "";
  // CAP-010: the client filter chips (All clients, or one client's slug), applied to the rows already fetched -- no new request.
  let clientFilter: string | null = null;
  let selected: string | null = ctx.route.kind === "meeting" ? ctx.route.id : null;
  let pane: Mounted | null = null;
  let canRename = false; // host or account admin: Rename and Delete meeting
  const deleted = new Set<string>(); // deleted here, or answered 410 meeting_deleted
  let flash: { text: string; ok: boolean } | null = null;
  let gone = false;
  const notesRoot = h("div", "pk5-notes");
  const canReviewIfAdmin = (ctx.me.capabilities ?? []).includes("account_admin");

  function mountNotes(): void {
    pane?.destroy();
    pane = null;
    notesRoot.replaceChildren();
    if (!selected) return;
    if (deleted.has(selected)) return; // the deleted page shows instead
    pane = mountSummary({
      root: notesRoot,
      meetingId: selected,
      timeZone: ctx.timeZone,
      canReviewIfAdmin,
      onNavigate: (view) => ctx.navigate(buildUrl(view, [])),
      recorderName: true,
    });
    canRename = canReviewIfAdmin;
    const id = selected;
    void fetchMeeting(id)
      .then((d) => {
        if (selected === id && d.is_host) {
          canRename = true;
          draw();
        }
      })
      .catch((err: unknown) => {
        // A meeting deleted (here or elsewhere): every route answers 410 meeting_deleted (A s.20).
        if (err instanceof ApiError && err.status === 410 && selected === id) {
          deleted.add(id);
          pane?.destroy();
          pane = null;
          notesRoot.replaceChildren();
          draw();
        }
      });
  }

  function pick(id: string): void {
    if (id === selected) return;
    selected = id;
    flash = null;
    history.replaceState(null, "", `/meetings/${id}`);
    mountNotes();
    draw();
  }

  /** A's GET /api/meetings/<id>/notes.txt: built and audited on the server (D); nothing is built in the browser. */
  async function download(): Promise<void> {
    if (!selected) return;
    let res: Response;
    try {
      res = await fetch(`/api/meetings/${encodeURIComponent(selected)}/notes.txt`, { credentials: "same-origin" });
    } catch {
      flash = { text: "Preekie couldn't be reached. Check your connection and try again.", ok: false };
      return draw();
    }
    if (!res.ok) {
      let message = "The notes weren't downloaded. Try again in a few minutes.";
      try {
        const body = (await res.json()) as { message?: string };
        if (body.message) message = body.message; // e.g. 404 notes_not_available, 503 when it can't be recorded
      } catch {
        /* not JSON */
      }
      flash = { text: message, ok: false };
      return draw();
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = h("a");
    a.href = url;
    a.download = downloadName(res.headers.get("Content-Disposition"), selected);
    document.body.append(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  function openRename(): void {
    const row = rows?.find((r) => r.meeting_id === selected);
    if (!row) return;
    const input = h("input", "pk5-input");
    input.maxLength = 120;
    input.value = row.title;
    const id = uid("pk5-rename");
    input.id = id;
    const label = h("label", "pk5-label", "Meeting name");
    label.htmlFor = id;
    const problem = h("p", "pk5-form-error");
    problem.setAttribute("role", "alert");
    problem.hidden = true;
    openDialog("Rename meeting", [h("div", "pk5-field", label, input), problem], [
      { label: "Cancel", variant: "secondary", safe: true, onClick: (close) => close() },
      {
        label: "Save",
        variant: "primary",
        onClick: async (close, btn) => {
          const bad = renameProblem(input.value);
          if (bad) {
            problem.textContent = bad;
            problem.hidden = false;
            return;
          }
          btn.disabled = true;
          try {
            const r = await apiRequest<{ meeting_id: string; title: string }>("POST", `/api/meetings/${encodeURIComponent(row.meeting_id)}/rename`, "The meeting wasn't renamed.", { title: input.value.trim() });
            row.title = r.title;
            flash = { text: "Meeting renamed.", ok: true };
            close();
            mountNotes();
            draw();
          } catch (err) {
            // 403 not_an_approver, 400 invalid_title: the server's words, dialog stays open.
            problem.textContent = messageOf(err, "The meeting wasn't renamed. Try again in a few minutes.");
            problem.hidden = false;
            btn.disabled = false;
          }
        },
      },
    ]);
    input.select();
  }

  async function openDelete(): Promise<void> {
    const row = rows?.find((r) => r.meeting_id === selected);
    if (!row) return;
    const id = row.meeting_id;
    // Whom the notes were handed over to: they lose access too (the mock-up's second line).
    let handedTo: string | null = null;
    try {
      handedTo = (await fetchSummary(id)).handed_over_to_name ?? null;
    } catch {
      /* the dialog still works without it */
    }
    const strong = (t: string) => h("strong", null, t);
    const [a, what, b] = DELETE_COPY.intro(`${row.title} · ${row.client_name}`);
    const body: HTMLElement[] = [h("p", null, a, strong(what), b)];
    if (handedTo) {
      const [c, name, d] = DELETE_COPY.handedOver(handedTo);
      body.push(h("p", null, c, strong(name), d));
    }
    const problem = h("p", "pk5-form-error");
    problem.setAttribute("role", "alert");
    problem.hidden = true;
    body.push(h("p", null, DELETE_COPY.kept), h("p", null, DELETE_COPY.cannotUndo), problem);
    openDialog(DELETE_COPY.title, body, [
      { label: DELETE_COPY.keep, variant: "secondary", safe: true, onClick: (close) => close() },
      {
        label: DELETE_COPY.confirm,
        variant: "destructive",
        onClick: async (close, btn) => {
          btn.disabled = true;
          btn.textContent = "Deleting…";
          try {
            await apiRequest("POST", `/api/meetings/${encodeURIComponent(id)}/delete`, "The meeting wasn't deleted.", {});
          } catch (err) {
            // 410: it was already deleted -- treat as done. 409 / 403 / 503 / 501: the server's words, dialog open.
            if (!(err instanceof ApiError && err.status === 410)) {
              problem.textContent = messageOf(err, "The meeting wasn't deleted. Try again in a few minutes.");
              problem.hidden = false;
              btn.disabled = false;
              btn.textContent = DELETE_COPY.confirm;
              return;
            }
          }
          deleted.add(id);
          rows = rows?.filter((r) => r.meeting_id !== id) ?? null;
          pane?.destroy();
          pane = null;
          notesRoot.replaceChildren();
          flash = null;
          close();
          draw();
          (ctx.root.querySelector(".pk5-deleted h2") as HTMLElement | null)?.focus();
        },
      },
    ]);
  }

  /** The ⋯ menu above the notes (the notes pane already shows the title, status and details). */
  function toolbar(): Node {
    const items: Array<{ label: string; onSelect: () => void }> = [{ label: MEETINGS_V05_COPY.download, onSelect: () => void download() }];
    if (canRename) {
      items.push({ label: "Rename", onSelect: openRename });
      items.push({ label: "Delete meeting", onSelect: () => void openDelete() });
    }
    const open = rows?.find((r) => r.meeting_id === selected);
    return h("div", "pk5-notes-bar", open ? h("span", "pk5-client-chip", open.client_name) : null, menuButton("More actions", items));
  }

  function draw(): void {
    if (gone) return;
    // CAP-010: Meetings / <client> / <meeting> for the open meeting.
    const open = selected ? rows?.find((r) => r.meeting_id === selected) : undefined;
    ctx.setCrumbs(open ? [{ label: "Meetings", href: "/meetings" }, { label: open.client_name }] : [{ label: "Meetings" }]);
    const search = h("input", "pk5-input");
    search.type = "search";
    search.placeholder = "Client, topic or person";
    search.setAttribute("aria-label", "Search meetings");
    search.value = query;
    search.addEventListener("input", () => {
      query = search.value;
      drawList();
    });
    const listBox = h("div", "pk5-meeting-list");
    const chips = h("div", "pk5-filter-chips");
    chips.setAttribute("role", "group");
    chips.setAttribute("aria-label", "Filter by client");
    const chipFor = (label: string, slug: string | null) => {
      const on = clientFilter === slug;
      const b = h("button", `pk5-filter-chip${on ? " pk5-filter-chip-on" : ""}`, on ? h("span", "pk5-filter-chip-tick", "✓") : null, label);
      b.type = "button";
      b.setAttribute("aria-pressed", on ? "true" : "false");
      b.querySelector(".pk5-filter-chip-tick")?.setAttribute("aria-hidden", "true");
      b.addEventListener("click", () => {
        clientFilter = slug;
        draw();
        ctx.root.querySelector<HTMLElement>('.pk5-filter-chip[aria-pressed="true"]')?.focus();
      });
      return b;
    };
    chips.append(chipFor("All clients", null), ...(ctx.me.entities ?? []).map((c) => chipFor(c.client_name, c.client_slug)));
    const drawList = () => {
      if (!rows) return listBox.replaceChildren(loading());
      const shown = rows.filter((r) => (clientFilter === null || r.client_slug === clientFilter) && matchesMeeting(r, query));
      // CAP-010 design (page 4): the client chip under the title; "date · state" on the right.
      listBox.replaceChildren(
        ...(shown.length
          ? shown.map((r) =>
              listRow(r.title, h("span", "pk5-client-chip", r.client_name), figure(`${r.length_minutes} min`), r.meeting_id === selected, () => pick(r.meeting_id), h("span", "pk5-muted pk5-row-date", `${shortDate(r.scheduled_start, ctx.timeZone)} · ${meetingStateLabel(r)}`))
            )
          : [emptyState(rows.length ? "No meetings match." : MEETINGS_V05_COPY.none, rows.length ? undefined : MEETINGS_V05_COPY.noneBody)]),
        ...(clientFilter !== null ? [filteredFooter(shown.length)] : [])
      );
    };
    /** "Showing 2 meetings for Demo Company (AU). Show all clients" while one client's chip is on. */
    function filteredFooter(n: number): HTMLElement {
      const name = (ctx.me.entities ?? []).find((c) => c.client_slug === clientFilter)?.client_name ?? "";
      const all = h("button", "pk5-link-btn", "Show all clients");
      all.type = "button";
      all.addEventListener("click", () => {
        clientFilter = null;
        draw();
        ctx.root.querySelector<HTMLElement>('.pk5-filter-chip[aria-pressed="true"]')?.focus();
      });
      return h("p", "pk5-filter-footer pk5-muted", `Showing ${n} ${n === 1 ? "meeting" : "meetings"} for ${name}. `, all);
    }
    drawList();
    const flashNode = flash ? notice(flash.text, flash.ok ? "status" : "alert") : null;
    let detail: Node;
    if (selected && deleted.has(selected)) {
      const t = h("h2", "pk5-detail-title", DELETE_COPY.deletedTitle);
      t.tabIndex = -1;
      const box = h("div", "pk5-detail pk5-deleted", t, h("p", null, DELETE_COPY.deletedBody));
      box.setAttribute("role", "status");
      detail = box;
    } else detail = selected ? h("div", "pk5-detail", toolbar(), flashNode, notesRoot) : emptyState(MEETINGS_V05_COPY.pick);
    ctx.root.replaceChildren(
      pageHead("Meetings", MEETINGS_V05_COPY.intro),
      ...(error ? [notice(error, "alert")] : []),
      splitPane("Meetings", [h("div", "pk5-meeting-search", search, ...((ctx.me.entities ?? []).length ? [chips] : [])), listBox], detail),
      retentionNote(),
      legalFooter()
    );
  }

  draw();
  mountNotes();
  void fetchMeetings([], true)
    .then((m) => {
      if (gone) return; // moved on before the list came back: leave the address (and history) alone
      rows = sortMeetings([...m.to_review, ...m.upcoming, ...(m.approved ?? [])]);
      if (!selected && rows[0]) {
        selected = rows[0].meeting_id;
        history.replaceState(null, "", `/meetings/${selected}`);
        mountNotes();
      }
    })
    .catch((err: unknown) => {
      error = messageOf(err, "Preekie couldn't load your meetings. Try again in a few minutes.");
      rows = [];
    })
    .then(draw);
  return () => {
    gone = true;
    pane?.destroy();
  };
}

SCREENS.meetings = meetings;
SCREENS.meeting = meetings;
